import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { LanguageCode, LocaleCode, SharedLanguageService, getTranslation } from '@language';
import { uniq } from 'lodash-es';
import { hasOwnPropertyGuard } from '../../../core/common/helpers/objects/has-own-property-guard.helper';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { mapToPaginatedResult } from '../../../overviews/generic-overview/components/pagination/helpers/map-to-paginated-result.helper';
import { ShCachingService } from '../../common/services/caching.service';
import { Paginated } from '../../http/entities/paginated.entity';
import { ShAbstractHttpService } from '../../http/services/http.abstract.service';
import { ShPaginationService } from '../../http/services/pagination.service';
import { ApiThesaurusConcept } from '../../thesaurus/entities/thesaurus-concept.entity';
import {
    BaseFlatFieldDefinition,
    BaseNestedFieldDefinition,
    DottedKey,
    FieldDefinition,
    ShFieldDefinition
} from '../entities/field-definition.entity';
import { FieldDefinitionHelper } from '../helpers/field-definition.helper';

@Injectable({ providedIn: 'root' })
export class ShFieldDefinitionService {
    private readonly SERVICE_NAME: string = 'ShFieldDefinitionService';

    protected readonly FALLBACK_VALUE: BaseNestedFieldDefinition[] = [];
    protected readonly TYPE_NAME: string = 'FieldDefinition';

    protected readonly DEFAULT_HEADERS: HttpHeaders = new HttpHeaders();
    protected readonly ENDPOINT: string = `v2/field-definitions`;

    // Services
    protected pagination: ShPaginationService = inject(ShPaginationService);
    protected httpService: ShAbstractHttpService = inject(ShAbstractHttpService);
    protected translateService: SharedLanguageService = inject(SharedLanguageService);
    protected cachingService: ShCachingService = inject(ShCachingService);

    constructor() {
        this.cachingService.register(this.SERVICE_NAME);
    }

    // #region Outward calls

    private fetchByProfileIds(profileIds: string[]): Observable<BaseNestedFieldDefinition[]> {
        let params: HttpParams = new HttpParams().append('nested', true);

        profileIds.forEach(profileId => {
            params = params.append('profiles', profileId);
        });

        return this.pagination
            .getAll<ShFieldDefinition>(this.ENDPOINT, params, this.DEFAULT_HEADERS, 500)
            .pipe(map(definitions => this.preProcessApiFieldDefinitions(definitions)));
    }

    private fetchByDottedKey(dottedKey: DottedKey): Observable<BaseNestedFieldDefinition> {
        const url: string = `${this.ENDPOINT}/${dottedKey}`;
        const params: HttpParams = new HttpParams().set('nested', true);
        return this.httpService
            .get<BaseNestedFieldDefinition>(url, params)
            .pipe(map(definition => this.preProcessApiFieldDefinitions([definition])[0]));
    }

    // Deprecated, so we won't be caching this
    public fetchByFlatKey(flatKey: string): Observable<BaseFlatFieldDefinition> {
        const url: string = `${this.ENDPOINT}/${flatKey}`;
        const params: HttpParams = new HttpParams().set('nested', true);
        return this.httpService
            .get<BaseNestedFieldDefinition>(url, params)
            .pipe(map(definition => this.preProcessApiFieldDefinitions([definition])[0]));
    }

    // #endregion

    // #region Inward calls
    public getByProfileIds(profileIds: string[], dottedKeys: DottedKey[]): Observable<BaseNestedFieldDefinition[]> {
        return this.cachingService.getMultipleWithPlaceholders(
            this.SERVICE_NAME,
            uniq(dottedKeys.map(FieldDefinitionHelper.getDottedKeyRoot)).filter((key): key is DottedKey => !!key),
            def => def.DottedKey,
            this.fetchByProfileIds.bind(this),
            profileIds
        );
    }

    public getByDottedKey(dottedKey: DottedKey): Observable<BaseNestedFieldDefinition | undefined> {
        const rootKey: DottedKey | undefined = FieldDefinitionHelper.getDottedKeyRoot(dottedKey);

        if (!rootKey) {
            return of(undefined);
        }

        return this.cachingService
            .getByIndex(this.SERVICE_NAME, rootKey, this.fetchByDottedKey.bind(this), rootKey)
            .pipe(map(rootDef => FieldDefinitionHelper.getNestedByDottedKey(rootDef, dottedKey)));
    }

    public getByDottedKeys(dottedKeys: DottedKey[]): Observable<BaseNestedFieldDefinition[]> {
        const fieldDefinitions: Observable<BaseNestedFieldDefinition | undefined>[] = dottedKeys.map(key =>
            this.getByDottedKey(key)
        );

        return combineLatest([...fieldDefinitions]).pipe(
            map(fieldDefinitions =>
                fieldDefinitions.filter((def): def is BaseNestedFieldDefinition => def !== undefined)
            )
        );
    }

    // #endregion

    protected preProcessApiFieldDefinitions(apiFieldDefinitions: ShFieldDefinition[]): BaseFlatFieldDefinition[] {
        const locale: LocaleCode = this.translateService.getCurrentLocale();
        return this.preProcessList(apiFieldDefinitions as BaseNestedFieldDefinition[], locale);
    }

    private preProcess(apiFieldDefinition: BaseNestedFieldDefinition, locale: LocaleCode): BaseNestedFieldDefinition {
        const Label: string = this.getFieldTranslation(apiFieldDefinition, 'Translations', locale);
        const LongLabel: string = this.getFieldTranslation(apiFieldDefinition, 'LongTranslations', locale);
        let children: BaseNestedFieldDefinition[] = [];

        if (apiFieldDefinition.Children && apiFieldDefinition.Children.length > 0) {
            children = this.preProcessList(apiFieldDefinition.Children as BaseNestedFieldDefinition[], locale);
        }

        return {
            ...apiFieldDefinition,
            Label,
            LongLabel,
            Children: children
        };
    }

    protected preProcessList(
        apiFieldDefinitions: BaseNestedFieldDefinition[],
        locale: LocaleCode
    ): BaseNestedFieldDefinition[] {
        return apiFieldDefinitions.map(apiFieldDefinition => this.preProcess(apiFieldDefinition, locale));
    }

    private getFieldTranslation(
        apiFieldDefinition: FieldDefinition,
        translationField: 'Translations' | 'LongTranslations',
        locale: LocaleCode
    ): string {
        const override: string | null = getTranslation(this.translateService, apiFieldDefinition.DottedKey);

        if (override) {
            return override;
        }

        if (apiFieldDefinition[translationField] && hasOwnPropertyGuard(apiFieldDefinition[translationField], locale)) {
            return apiFieldDefinition[translationField][locale];
        }

        /* If language cannot be found, use the flatkey */
        return apiFieldDefinition.FlatKey;
    }

    // #endregion

    // #endregion

    // #endregion

    // #region ThesaurusField
    public getThesaurusFieldConcepts(
        thesaurusDefDottedKey: DottedKey,
        searchTerm: string,
        language?: LanguageCode,
        nrOfResults: number = 100
    ): Observable<ApiThesaurusConcept[]> {
        const thesaurusFieldEndpoint = `${this.ENDPOINT}/${thesaurusDefDottedKey}/concepts`;
        let searchParams: HttpParams = new HttpParams().append('q', searchTerm);

        if (language) {
            searchParams = searchParams.append('lang', language);
        }

        return this.pagination
            .get<ApiThesaurusConcept>(thesaurusFieldEndpoint, searchParams, undefined, undefined, nrOfResults)
            .pipe(map(response => response.Results));
    }

    public getThesaurusFieldConcept(
        thesaurusDefDottedKey: DottedKey,
        thesaurusConceptId: string
    ): Observable<ApiThesaurusConcept> {
        const thesaurusFieldEndpoint = `${this.ENDPOINT}/${thesaurusDefDottedKey}/concepts/${thesaurusConceptId}`;

        // Can potentially fail due to bug or concept being removed
        // Doing multiple retries makes opening of Thesaurus Concept Dialog seem unresponsive
        // Better to quit early
        return this.httpService.get<ApiThesaurusConcept>(thesaurusFieldEndpoint);
    }

    public getThesaurusFieldNarrowerConcepts(
        thesaurusDefDottedKey: DottedKey,
        thesaurusConceptId: string,
        startIndex: number = 0
    ): Observable<PaginatedResults<ApiThesaurusConcept>> {
        const thesaurusFieldEndpoint = `${this.ENDPOINT}/${thesaurusDefDottedKey}/concepts/${thesaurusConceptId}/narrower`;

        const searchParams: HttpParams = new HttpParams()
            .append('nrOfResults', '100')
            .append('startIndex', startIndex.toString());

        return this.httpService
            .get<Paginated<ApiThesaurusConcept>>(thesaurusFieldEndpoint, searchParams)
            .pipe(map(paginated => mapToPaginatedResult(paginated, 100)));
    }

    // #endregion
}
